import { Button } from '@components/Button';
import { ErrorMessage, FormLabel, InputField, PhoneInputField } from '@components/FormFields';
import { CreateLoftyLeadDto } from '@engelvoelkersamericas/api';
import { download } from '@util/download';
import { Checkbox } from 'antd';
import { apiClient } from 'api-client';
import { useLocale, useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';
import React, { FC, useState, useEffect } from 'react';
import { sanitizedNumber } from 'util/phoneNumber';
import {
  CustomButtonWrapper,
  DisclaimerAndButtonContaienr,
  DisclaimerContainer,
  DisclaimerText,
  FieldWrapper,
  ReadMore,
  StyledForm,
} from './styles';
import { useStores } from 'mobx/useStores';

export interface UserData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

interface EmbeddedFormProps {
  userData?: UserData;
  setFormSuccess: (success: boolean) => void;
  media?: {
    url: string;
    fileName: string;
  };
  disclaimer: string;
  handleDownloadClick: () => void;
  chimeId?: string;
  chimeType?: string;
  ownershipId?: string;
  ownershipScope?: string;
  welcomeEmail?: boolean;
}

const EmbeddedForm: FC<EmbeddedFormProps> = ({
  userData,
  setFormSuccess,
  media,
  disclaimer,
  handleDownloadClick,
  chimeId,
  chimeType,
  ownershipId,
  ownershipScope,
  welcomeEmail,
}) => {
  const locale = useLocale();
  const t = useTranslations();
  const pathname = usePathname();
  const { appStore, shopStore } = useStores();
  const { validSubdomainData } = appStore;
  const { shopChimeId } = shopStore;

  const [firstNameError, setFirstNameError] = React.useState('');
  const [lastNameError, setLastNameError] = React.useState('');
  const [phoneError, setPhoneError] = React.useState('');
  const [emailError, setEmailError] = React.useState('');
  const [locationError, setLocationError] = React.useState('');
  const [isChecked, setIsChecked] = React.useState(false);
  const [url, setURL] = useState('');
  const [phoneNumber, setPhoneNumber] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);

  useEffect(() => {
    setURL(`${window.location.origin}${pathname}`);
  }, []);

  const toggleReadMore = () => {
    setIsOpen(!isOpen);
  };

  const handlePhoneInputChange = (e) => {
    setPhoneNumber(e ?? '');
  };
  const handleInputChange = (e) => {
    if (e?.target?.id == 'firstName') {
      setFirstNameError('');
    }

    if (e?.target?.id == 'lastName') {
      setLastNameError('');
    }

    if (e?.target?.id == 'email') {
      setEmailError('');
    }

    if (e?.target?.id == 'location') {
      setLocationError('');
    }
  };

  const localeDisclaimer = disclaimer ?? '';

  const handleDownload = async (event) => {
    // Stop the form from submitting and refreshing the page.
    event.preventDefault();
    setFirstNameError('');
    setLastNameError('');
    setEmailError('');
    setPhoneError('');
    setLocationError('');

    // Get data from the form.
    const data = {
      firstName: userData ? userData?.firstName : event?.target?.firstName?.value,
      lastName: userData ? userData?.lastName : event?.target?.lastName?.value,
      phone: userData ? userData?.phone : event?.target?.phone?.value,
      email: userData ? userData?.email : event?.target?.email?.value,
      location: event?.target?.location?.value,
    };

    //validations
    let formIsValid = true;
    setFirstNameError('');
    if (!data.firstName) {
      formIsValid = false;
      setFirstNameError('First Name cannot be empty');
    }

    if (!data.lastName) {
      setLastNameError('');
      formIsValid = false;
      setLastNameError('Last Name cannot be empty');
    }

    //Email
    if (!data.email) {
      formIsValid = false;
      setEmailError('Email cannot be empty');
    }

    if (typeof data.email !== 'undefined') {
      const emailFormat = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g;

      if (!data.email.match(emailFormat)) {
        formIsValid = false;
        setEmailError('Email is not valid');
      }
    }

    if (!data.phone) {
      formIsValid = false;
      setPhoneError('Phone cannot be empty');
    }

    if (!data.location) {
      formIsValid = false;
      setLocationError('Location cannot be empty');
    }

    if (formIsValid) {
      if (media) {
        handleDownloadClick();
        await download(`https:${media.url}`, media.fileName);
      }

      setFirstNameError('');
      setLastNameError('');
      setEmailError('');
      setPhoneError('');
      setLocationError('');

      // Send the data to the server in JSON format.

      const chimeObj: CreateLoftyLeadDto = {
        userFirstName: data.firstName,
        userLastName: data.lastName,
        userPhone: sanitizedNumber(data.phone),
        userEmail: data.email,
        location: data.location,
        chimeId: chimeId ? parseInt(chimeId, 10) : undefined,
        type: chimeType ? chimeType : 'EV Global',
        ownershipId: ownershipId ? parseInt(ownershipId, 10) : undefined,
        welcomeEmail: !!welcomeEmail,
      };

      if (url) {
        chimeObj.url = url;
      }

      // If we are on a shop subdomain, then set the ownershipId to the shop's chimeId
      if (
        validSubdomainData &&
        validSubdomainData.type?.toLowerCase() !== 'advisor' &&
        chimeType?.toLowerCase() === 'shop' &&
        shopChimeId
      ) {
        chimeObj.ownershipId = shopChimeId;
      }
      if (chimeObj.ownershipId) {
        chimeObj.ownershipScope = ownershipScope || 'OFFICE';
      }

      const results = await apiClient.createLoftyLeads({
        createLoftyLeadDto: chimeObj,
      });
      setFormSuccess(results ? true : false);

      //For better debugging
      console.log('Data sent: ', chimeObj);
      console.log('Results: ', results);
    }
  };

  return (
    <>
      <StyledForm onSubmit={handleDownload}>
        {!userData && (
          <>
            <FieldWrapper>
              <FormLabel htmlFor="firstName">{t('First Name')} *</FormLabel>
              <InputField
                id="firstName"
                placeholder={t('First Name')}
                type="text"
                $isError={firstNameError != '' ? true : false}
                onChange={(e) => handleInputChange(e)}
              />
              {firstNameError != '' && <ErrorMessage message={firstNameError} />}
            </FieldWrapper>
            <FieldWrapper>
              <FormLabel htmlFor="lastName">{t('Last Name')} *</FormLabel>
              <InputField
                id="lastName"
                placeholder={t('Last Name')}
                $isError={lastNameError != '' ? true : false}
                onChange={(e) => handleInputChange(e)}
              />
              {lastNameError != '' && <ErrorMessage message={lastNameError} />}
            </FieldWrapper>
            <FieldWrapper>
              <FormLabel htmlFor="email">{t('Email')} *</FormLabel>
              <InputField
                id="email"
                placeholder={'you@emailaddress.com'}
                onChange={(e) => handleInputChange(e)}
                $isError={emailError != '' ? true : false}
              />
              {emailError != '' && <ErrorMessage message={emailError} />}
            </FieldWrapper>
            <FieldWrapper>
              <FormLabel htmlFor="phone">{t('Phone')} *</FormLabel>
              <PhoneInputField
                locale={locale}
                id="phone"
                isError={phoneError != '' ? true : false}
                placeholder="(123)-456-7890"
                onChange={(e) => handlePhoneInputChange(e)}
                value={phoneNumber}
              />
              {phoneError != '' && <ErrorMessage message={phoneError} />}
            </FieldWrapper>
          </>
        )}
        <FieldWrapper>
          <FormLabel>{t('Location')} *</FormLabel>
          <InputField
            id="location"
            placeholder={t('City')}
            maxLength={250}
            $isError={locationError != '' ? true : false}
            onChange={(e) => handleInputChange(e)}
          />
          {locationError != '' && <ErrorMessage message={locationError} />}
        </FieldWrapper>
        <DisclaimerAndButtonContaienr>
          <DisclaimerContainer>
            <Checkbox
              style={{
                alignSelf: 'start',
                fontWeight: 400,
                lineHeight: '1.5rem',
              }}
              onClick={() => setIsChecked(!isChecked)}
              key="data-processing-consent"
              title="data-processing-consent"
            />
            <span style={{ color: '#E60000' }}>*</span>
            <DisclaimerText>
              {localeDisclaimer && (
                <>
                  <span>{!isOpen ? localeDisclaimer.slice(0, 217) + '...' : localeDisclaimer}</span>
                </>
              )}
              <ReadMore onClick={toggleReadMore}>{isOpen ? 'Read Less' : 'Read More'}</ReadMore>
            </DisclaimerText>
          </DisclaimerContainer>
          <CustomButtonWrapper>
            <Button
              type="submit"
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              variant={'secondaryRed'}
              label={t('Download')}
              arrow={false}
              disabled={!isChecked}
              icon="Download"
              iconPosition="left"
            />
          </CustomButtonWrapper>
        </DisclaimerAndButtonContaienr>
      </StyledForm>
    </>
  );
};

export default EmbeddedForm;
